import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Typography, Grid, Box } from '@mui/material';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useTranslation } from 'react-i18next';
import { getInboundEmails, toggleInboundEmails, patchInboundEmails } from 'entities/inbound-emails/api';
import { Paper } from 'shared/ui/Paper';
import { Routes } from 'app/config/routes/consts';
import { Switch } from 'shared/ui/Switch';
import { PageHeader } from 'shared/ui/PageHeader';
import { Button } from 'shared/ui/Button';
import { useModelsOptions } from 'shared/hooks/use-models-options';
import { ColorPalette } from 'shared/consts/colorPalette';
import Clipboard from 'shared/ui/clipboard';
import { InboundEmail as InboundEmailType } from 'entities/inbound-emails/types';
import AdditionForm from './partials/addition-form';
import EditForm from './partials/edit-height-form';
import AdaptiveContent from 'shared/ui/adaptive-content';
import { EditFormType } from './partials/edit-height-form/type';
import { PageWrapper } from 'pages/_layouts/PageWrapper';

const InboundEmail: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { getModelName } = useModelsOptions();

	const [isFormVisible, setIsFormVisible] = useState(false);

	const { data: inboundEmailsResponse } = useQuery({
		queryKey: ['get/inbound-emails'],
		queryFn: () => getInboundEmails(),
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: toggleMutate } = useMutation({
		mutationKey: ['patch/toggle-inbound-emails'],
		mutationFn: toggleInboundEmails,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['get/inbound-emails'] });
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: patchMutate } = useMutation({
		mutationKey: ['patch/inbound-emails'],
		mutationFn: patchInboundEmails,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['get/inbound-emails'] });
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const onSwitchChange = (id: string) => {
		toggleMutate({ id });
	};

	const renderModelName = (data: InboundEmailType) => {
		const name = getModelName(data.generativeModelId || data.customModelId || data.category);
		return name;
	};

	const submitEditForm = ({ id, data }: { id: string; data: EditFormType }) => {
		patchMutate({ id, data });
	};

	return (
		<PageWrapper>
			<PageHeader
				title={t('extensions.ex_list.inbound_email.title')}
				description={t('extensions.ex_list.inbound_email.description')}
				backBtn
				onClick={() => navigate(Routes.extensions)}
			/>
			{isFormVisible ? (
				<AdditionForm closeForm={() => setIsFormVisible(false)} />
			) : (
				<>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2.5 }}>
						<Button size="small" onClick={() => setIsFormVisible(true)}>
							{t('extensions.ex_list.inbound_email.add_new_email')}
						</Button>
					</Box>

					<Grid container direction="column" spacing={5}>
						{inboundEmailsResponse?.data?.map((item) => (
							<AdaptiveContent key={item._id}>
								<Paper
									sx={{
										'width': '100%',
										'display': 'flex',
										'flexDirection': 'column',
										'gap': 2.5,
										'padding': '30px',
										'& > div:not(:last-child)': {
											paddingBottom: '20px',
											borderBottom: `1px solid ${ColorPalette.periwinkle}`,
										},
									}}
								>
									<Grid
										container
										spacing={5}
										alignItems="center"
										sx={{ minHeight: '65px' }}
									>
										<Grid size={6}>
											<Typography variant={'h5'} color={ColorPalette.astronaut}>
												{t('extensions.ex_list.inbound_email.set_up')}
											</Typography>
										</Grid>
										<Grid size={6} textAlign="right">
											<Switch
												checked={item.isEnabled}
												onChange={() => onSwitchChange(item._id)}
											/>
										</Grid>
									</Grid>

									<Grid
										container
										spacing={5}
										alignItems="center"
										sx={{ minHeight: '65px' }}
									>
										<Grid size={6}>
											<Typography variant={'body2'} color={ColorPalette.astronaut}>
												{t('extensions.ex_list.inbound_email.email_address')}
											</Typography>
										</Grid>
										<Grid size={6}>
											<Clipboard
												text={item.emailAddress}
												sx={{ justifyContent: 'flex-end' }}
												content={
													<Typography
														variant="body2"
														color={ColorPalette.astronaut}
														sx={{
															maxWidth: '100%',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{item.emailAddress}
													</Typography>
												}
											/>
										</Grid>
									</Grid>

									<Grid
										container
										spacing={5}
										alignItems="center"
										sx={{ minHeight: '65px' }}
									>
										<Grid size={6}>
											<Typography variant={'body2'} color={ColorPalette.astronaut}>
												{t('extensions.ex_list.inbound_email.model')}
											</Typography>
										</Grid>
										<Grid size={6} textAlign="right">
											<Typography variant={'body2'} color={ColorPalette.astronaut}>
												{renderModelName(item)}
											</Typography>
										</Grid>
									</Grid>

									<Grid
										container
										spacing={5}
										alignItems="center"
										sx={{ minHeight: '65px' }}
									>
										<Grid size={6}>
											<Typography variant={'body2'} color={ColorPalette.astronaut}>
												{t('extensions.ex_list.inbound_email.image_height')}
											</Typography>
										</Grid>
										<Grid size={6} container alignItems="center" justifyContent="right">
											<EditForm emailDetails={item} onSubmit={submitEditForm} />
										</Grid>
									</Grid>

									<Grid>
										<Grid
											container
											spacing={5}
											alignItems="center"
											sx={{ minHeight: '65px' }}
										>
											<Grid size={6}>
												<Typography variant={'body2'} color={ColorPalette.astronaut}>
													{t('extensions.ex_list.inbound_email.extract_email')}
												</Typography>
											</Grid>
											<Grid size={6} textAlign="right">
												<Switch
													checked={item.isHandleEmailBody}
													onChange={() => {
														patchMutate({
															id: item._id,
															data: {
																isHandleEmailBody: !item.isHandleEmailBody,
															},
														});
													}}
												/>
											</Grid>
										</Grid>

										{item.isHandleEmailBody && (
											<Grid
												container
												spacing={5}
												alignItems="center"
												sx={{
													paddingLeft: 2.5,
													borderLeft: `1px solid ${ColorPalette.zircon}`,
												}}
											>
												<Grid size={6}>
													<Typography
														variant={'body2'}
														color={ColorPalette.baliHai}
													>
														{t(
															'extensions.ex_list.inbound_email.extract_original_email',
														)}
													</Typography>
												</Grid>
												<Grid size={6} textAlign="right">
													<Switch
														checked={!!item.isExtractOriginalEmail}
														onChange={() => {
															patchMutate({
																id: item._id,
																data: {
																	isExtractOriginalEmail:
																		!item.isExtractOriginalEmail,
																},
															});
														}}
													/>
												</Grid>
											</Grid>
										)}
									</Grid>
								</Paper>
							</AdaptiveContent>
						))}
					</Grid>
				</>
			)}
		</PageWrapper>
	);
};

export default InboundEmail;
